import { useState, useEffect, useCallback } from "react";
import { RotateCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "../api.js";
import JournalRecordModal from "../components/JournalRecordModal.jsx";
import JournalCalendar from "../components/JournalCalendar.jsx";
import { mskTodayStr } from "../lib/mskDate.js";

const todayStr = mskTodayStr;

// Чистая календарная арифметика через Date.UTC — без сравнений с локальным часовым поясом
// (та же осторожность, что и с todayInMoscow() на бэкенде: тут дата не привязана к моменту суток).
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d) + n * 86400000);
  return dt.toISOString().slice(0, 10);
}

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split("-");
  return `${d}.${m}.${y}`;
}

function pluralizeRecords(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "запись";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "записи";
  return "записей";
}

export default function JournalPage({ onStartExam, onNavigateToPatient }) {
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [records, setRecords] = useState(null); // null = загрузка
  const [staffList, setStaffList] = useState(null); // null = загрузка; не зависит от даты
  const [recordsError, setRecordsError] = useState("");
  const [staffError, setStaffError] = useState("");
  const [activeRecord, setActiveRecord] = useState(null);

  const load = useCallback(() => {
    setRecords(null);
    setRecordsError("");
    api.getYclientsRecordsToday(selectedDate)
      .then(r => setRecords(r.records || []))
      .catch((error) => {
        setRecordsError(error.response?.data?.error || error.message || "Неизвестная ошибка");
        setRecords([]);
      });
  }, [selectedDate]);

  // Список сотрудников не зависит от даты, но должен обновляться вместе с записями по кнопке
  // «Обновить» — иначе один неудачный запрос при старте (например, сразу после автообновления
  // приложения, пока сеть ещё не устаканилась) навсегда оставляет staffList пустым на всю сессию,
  // и все записи молча падают в колонку «Без сотрудника» без способа это исправить.
  const loadStaff = useCallback(() => {
    setStaffError("");
    api.getYclientsStaff().then(r => setStaffList(r.staff || [])).catch((error) => {
      setStaffError(error.response?.data?.error || error.message || "Неизвестная ошибка");
      setStaffList([]);
    });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStaff(); }, [loadStaff]);

  const refresh = () => { load(); loadStaff(); };

  const isToday = selectedDate === todayStr();

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white flex-shrink-0 gap-3 flex-wrap">
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-lg font-bold text-slate-900">
            Журнал записей{isToday ? " на сегодня" : ` на ${formatDate(selectedDate)}`}
          </h2>
          {records !== null && records.length > 0 && (
            <span className="text-xs font-medium text-slate-400">{records.length} {pluralizeRecords(records.length)}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSelectedDate(d => addDays(d, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <input
            type="date"
            value={selectedDate}
            onChange={e => e.target.value && setSelectedDate(e.target.value)}
            className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
          />
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSelectedDate(d => addDays(d, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isToday && (
            <Button size="sm" variant="ghost" onClick={() => setSelectedDate(todayStr())}>Сегодня</Button>
          )}
          <Button size="sm" variant="ghost" className="gap-1.5" onClick={refresh}>
            <RotateCw className="h-3.5 w-3.5" />
            Обновить
          </Button>
        </div>
      </div>

      {records === null || staffList === null ? (
        <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-400">
          <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" />
          Загрузка...
        </div>
      ) : (
        <div className="flex-1 overflow-hidden p-4 flex flex-col gap-3">
          {(recordsError || staffError) && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <div className="font-semibold">Не удалось загрузить журнал из YClients</div>
              <div className="mt-1 break-words">{[recordsError, staffError].filter((value, index, all) => value && all.indexOf(value) === index).join("; ")}</div>
            </div>
          )}
          <JournalCalendar
            staffList={staffList}
            records={records}
            onSelectRecord={setActiveRecord}
            isToday={isToday}
          />
        </div>
      )}

      {activeRecord && (
        <JournalRecordModal
          record={activeRecord}
          onStartExam={(record) => { setActiveRecord(null); onStartExam(record); }}
          onClose={() => setActiveRecord(null)}
          onNavigateToPatient={onNavigateToPatient ? (patientId) => { setActiveRecord(null); onNavigateToPatient(patientId); } : null}
        />
      )}
    </div>
  );
}
